/**
 * RBAC Permissions Utility
 *
 * Defines the simplified 4-tier role system:
 * - Admin: Full CRUD permissions
 * - Editor: Read & Update permissions
 * - Viewer: Read-only permissions
 * - Custom: User-defined permissions
 *
 * NOTE: Roles only apply to Tenant Admins (type=2)
 *       SuperAdmins (type=1) bypass all role checks
 *       MobileClients (type=4) use a different permission model
 */

export const ROLE_TYPES = {
  ADMIN: 'Admin',
  EDITOR: 'Editor',
  VIEWER: 'Viewer',
  CUSTOM: 'Custom',
} as const;

export type RoleType = typeof ROLE_TYPES[keyof typeof ROLE_TYPES];

export type Permission = 'create' | 'read' | 'update' | 'delete';

/**
 * Permission mapping for each role type
 */
export const ROLE_PERMISSIONS: Record<RoleType, Permission[]> = {
  Admin: ['create', 'read', 'update', 'delete'],
  Editor: ['read', 'update'],
  Viewer: ['read'],
  Custom: ['read'], // User-defined (read always enabled)
};

/**
 * Get permissions array for a given role type
 */
export const getRolePermissions = (roleType: RoleType): Permission[] => {
  return ROLE_PERMISSIONS[roleType] || [];
};

/**
 * Check if a user has a specific permission based on their permissions array
 */
export const hasPermission = (permissions: string[], permission: Permission): boolean => {
  return permissions.includes(permission);
};

/**
 * Check if a user can create resources
 */
export const canCreate = (permissions: string[]): boolean => {
  return hasPermission(permissions, 'create');
};

/**
 * Check if a user can read resources
 */
export const canRead = (permissions: string[]): boolean => {
  return hasPermission(permissions, 'read');
};

/**
 * Check if a user can update resources
 */
export const canUpdate = (permissions: string[]): boolean => {
  return hasPermission(permissions, 'update');
};

/**
 * Check if a user can delete resources
 */
export const canDelete = (permissions: string[]): boolean => {
  return hasPermission(permissions, 'delete');
};

/**
 * Infer role type from permissions array (for backward compatibility)
 */
export const getRoleTypeFromPermissions = (permissions: string[]): RoleType => {
  const permSet = new Set(permissions);

  if (permSet.has('create') && permSet.has('delete')) {
    return ROLE_TYPES.ADMIN;
  } else if (permSet.has('update') && !permSet.has('create')) {
    return ROLE_TYPES.EDITOR;
  } else if (permSet.size === 1 && permSet.has('read')) {
    return ROLE_TYPES.VIEWER;
  } else {
    return ROLE_TYPES.CUSTOM;
  }
};

/**
 * Get human-readable description of a role type
 */
export const getRoleDescription = (roleType: RoleType): string => {
  const descriptions: Record<RoleType, string> = {
    Admin: 'Full access - Can create, read, update, and delete resources',
    Editor: 'Limited access - Can read and update resources',
    Viewer: 'Read-only access - Can only view resources',
    Custom: 'Custom permissions - Manually configured access',
  };
  return descriptions[roleType] || 'Unknown role type';
};

/**
 * Format permissions as a human-readable string
 */
export const formatPermissions = (permissions: string[]): string => {
  if (permissions.length === 0) return 'No permissions';

  const labels: Record<string, string> = {
    create: 'Create',
    read: 'Read',
    update: 'Update',
    delete: 'Delete',
  };

  return permissions.map((p) => labels[p] || p).join(', ');
};
